import { StyleSheet } from 'react-native';
import type { ComponentProps, ReactNode } from 'react';
import { YStack } from 'tamagui';
import { AppTopBar } from './AppTopBar';
import { FallbackImage } from './FallbackImage';

interface HeroSectionProps {
  /** Hero image URL — rendered via `FallbackImage` (gradient + initial
   *  letter when the image is missing). Pass `imageSlot` instead to
   *  supply custom image content (carousel, scroll-animated image). */
  image?: string | undefined;
  /** Fallback initial / label when the image fails to load. */
  imageLabel?: string | undefined;
  /** Render custom image-layer content instead of the built-in
   *  `FallbackImage`. The caller is responsible for `absoluteFill`
   *  styling. Use for photo carousels (trip detail) or scroll-linked
   *  animated images + overlays (landing). */
  imageSlot?: ReactNode | undefined;
  /** Fixed pixel height for the hero. */
  height?: number | undefined;
  /** Minimum height — the hero grows with content past this. Use for
   *  card-style heroes where the body text drives the actual height
   *  (landing). */
  minHeight?: number | undefined;
  /** Aspect ratio used when neither `height` nor `minHeight` is set.
   *  Defaults to 1.4 — the wide-cinematic shape used by detail screens. */
  aspectRatio?: number | undefined;
  /** Optional fixed height applied at the `lg` breakpoint (web only)
   *  so an aspectRatio-based hero doesn't grow with the column past
   *  a sensible bound. */
  largeHeight?: number | undefined;
  /** Horizontal margin around the hero. Defaults to 0 (edge-to-edge);
   *  set to inset the hero as a card (landing). */
  marginHorizontal?: number | undefined;
  /** Hero corner radius. Defaults to 0; set for card-shaped heroes. */
  borderRadius?: number | undefined;
  /** Fill the parent's height (`height: '100%'`) instead of sizing
   *  itself. Use when an animated wrapper owns the height — e.g. the
   *  resizable trip-detail hero. Takes precedence over
   *  height/minHeight/aspectRatio. */
  fill?: boolean | undefined;
  /** Render the back arrow inside the embedded AppTopBar. */
  showBack?: boolean | undefined;
  /** Per-screen actions rendered in the AppTopBar's right slot. */
  rightSlot?: ReactNode | undefined;
  /** Placeholder colour shown until the image loads. */
  backgroundColor?: ComponentProps<typeof YStack>['backgroundColor'] | undefined;
  /** Overlay content rendered on top of the image, after the
   *  AppTopBar (so absolute children stack above the bar visually). */
  children?: ReactNode | undefined;
}

/**
 * Reusable hero shell — image + embedded `AppTopBar` overlay. Every
 * page with a hero image (detail screens, profile, legal pages, etc.)
 * goes through this primitive so the bar always sits inside the hero
 * and back-arrow / right-slot wiring is uniform.
 *
 * Two shapes:
 *  - `height={N}` — fixed pixel hero (profile, landing card).
 *  - `aspectRatio={1.4}` (default) — wide cinematic hero used by every
 *    `HeroStrip` caller (place / journey / stay / placeholder).
 *
 * Anything passed as `children` renders absolute-positioned on top of
 * the image, after the AppTopBar — so it stacks below the bar visually
 * but above the photo.
 */
export function HeroSection({
  image,
  imageLabel,
  imageSlot,
  height,
  minHeight,
  aspectRatio,
  largeHeight,
  marginHorizontal,
  borderRadius,
  fill,
  showBack,
  rightSlot,
  backgroundColor = '$borderMuted',
  children,
}: HeroSectionProps) {
  // `fill` wins: the parent (an animated wrapper) owns the height.
  // Otherwise pick exactly one of height / minHeight / aspectRatio. If
  // multiple are set the order is height > minHeight > aspectRatio (most
  // restrictive wins). Default to aspectRatio 1.4 — the wide
  // detail-screen shape.
  const dimensions = fill
    ? { height: '100%' as const }
    : height != null
      ? { height }
      : minHeight != null
        ? { minHeight }
        : { aspectRatio: aspectRatio ?? 1.4 };
  // `$lg` is the web-only override — only meaningful for aspect-ratio
  // mode where we want to cap growth on wide viewports. Skipped under
  // `fill` (the wrapper owns the height across all breakpoints).
  const responsive =
    !fill && largeHeight != null
      ? { $lg: { aspectRatio: undefined as unknown as number, height: largeHeight } }
      : undefined;
  // overflow: 'hidden' clips the image to the rounded corners when
  // a card-style hero is in use.
  const cardClipping = borderRadius != null ? { overflow: 'hidden' as const } : undefined;
  return (
    <YStack
      width={marginHorizontal != null ? undefined : '100%'}
      marginHorizontal={marginHorizontal}
      borderRadius={borderRadius}
      position="relative"
      backgroundColor={backgroundColor}
      {...dimensions}
      {...responsive}
      {...cardClipping}
    >
      {imageSlot ??
        (image != null ? (
          <FallbackImage
            uri={image}
            label={imageLabel ?? ''}
            style={StyleSheet.absoluteFill}
            letterSize={52}
            contentFit="cover"
            transition={200}
          />
        ) : null)}
      <AppTopBar showBack={showBack} rightSlot={rightSlot} />
      {children}
    </YStack>
  );
}
